// Performance profiler to identify LCP bottlenecks
export class PerformanceProfiler {
  private static marks: Map<string, number> = new Map();
  private static measures: Array<{ name: string, duration: number, startTime: number }> = [];

  static mark(name: string) {
    const timestamp = performance.now();
    this.marks.set(name, timestamp);
    console.log(`🔍 PERF MARK: ${name} at ${timestamp.toFixed(2)}ms`);
  }

  static measure(name: string, startMark: string) {
    const startTime = this.marks.get(startMark);
    if (!startTime) {
      console.warn(`Start mark "${startMark}" not found`);
      return;
    }

    const endTime = performance.now();
    const duration = endTime - startTime;

    this.measures.push({ name, duration, startTime });
    console.log(`📊 PERF MEASURE: ${name} took ${duration.toFixed(2)}ms`);

    return duration;
  }

  static getReport() {
    const report = {
      marks: Array.from(this.marks.entries()),
      measures: this.measures.sort((a, b) => b.duration - a.duration),
      totalTime: performance.now()
    };

    console.log('📈 PERFORMANCE REPORT:', report);
    return report;
  }

  static trackLCP() {
    // Track LCP using Performance Observer
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'largest-contentful-paint') {
            console.log(`🎯 LCP: ${entry.startTime.toFixed(2)}ms`);
            // Cast to proper type for LCP entry
            const lcpEntry = entry as any;
            if (lcpEntry.element) {
              console.log(`🎯 LCP Element:`, lcpEntry.element);
            }
            if (lcpEntry.url) {
              console.log(`🎯 LCP URL:`, lcpEntry.url);
            }
          }
        }
      });

      observer.observe({ entryTypes: ['largest-contentful-paint'] });
    }

    // Also track using Web Vitals if available
    if ('webVitals' in window) {
      // @ts-ignore
      window.webVitals.getLCP((metric: any) => {
        console.log(`🎯 Web Vitals LCP: ${metric.value}ms`);
      });
    }
  }

  static trackResourceLoading() {
    // Track all resource loading times
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'resource') {
            const resource = entry as PerformanceResourceTiming;
            const duration = resource.responseEnd - resource.startTime;

            if (duration > 1000) { // Only log resources taking over 1 second
              console.log(`🐌 SLOW RESOURCE: ${resource.name} took ${duration.toFixed(2)}ms`);
            }
          }
        }
      });

      observer.observe({ entryTypes: ['resource'] });
    }
  }

  static trackNavigationTiming() {
    // Track detailed navigation timing
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

    if (navigation) {
      const timings = {
        dnsLookup: navigation.domainLookupEnd - navigation.domainLookupStart,
        tcpConnection: navigation.connectEnd - navigation.connectStart,
        serverResponse: navigation.responseEnd - navigation.requestStart,
        domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
        domComplete: navigation.domComplete - navigation.fetchStart,
        pageLoad: navigation.loadEventEnd - navigation.fetchStart
      };

      console.log('🌐 NAVIGATION TIMINGS:', timings);

      // Identify bottlenecks
      Object.entries(timings).forEach(([name, time]) => {
        if (time > 2000) {
          console.warn(`⚠️ SLOW ${name.toUpperCase()}: ${time.toFixed(2)}ms`);
        }
      });
    }
  }

  static trackProductionBottlenecks() {
    // Track production-specific issues
    console.log('🌍 Environment:', {
      isDevelopment: process.env.NODE_ENV === 'development',
      isProduction: process.env.NODE_ENV === 'production',
      hasNetworkInfo: 'navigator' in window && 'connection' in navigator,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      // Check for Vercel runtime environment
      vercelRegion: process.env.VERCEL_REGION || 'unknown',
      vercelUrl: process.env.VERCEL_URL || 'unknown',
      isVercel: typeof process !== 'undefined' && process.env.VERCEL === '1'
    });

    // Track network information if available
    if ('navigator' in window && 'connection' in (navigator as any)) {
      const connection = (navigator as any).connection;
      console.log('📡 Network Info:', {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt,
        saveData: connection.saveData
      });
    }

    // Detect region by making a test request to a Vercel edge function
    this.detectVercelRegion();

    // TEMPORARILY DISABLED: Track API response times - causing JSON parsing issues
    console.log('⚠️ Performance profiler fetch interceptor disabled for debugging');
    /*
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const startTime = performance.now();
      const url = args[0]?.toString() || 'unknown';
      
      try {
        const response = await originalFetch(...args);
        const duration = performance.now() - startTime;
        
        if (duration > 2000) {
          console.warn(`🐌 SLOW API CALL: ${url} took ${duration.toFixed(2)}ms`);
        } else {
          console.log(`⚡ API CALL: ${url} took ${duration.toFixed(2)}ms`);
        }
        
        return response;
      } catch (error) {
        const duration = performance.now() - startTime;
        console.error(`❌ FAILED API CALL: ${url} failed after ${duration.toFixed(2)}ms`, error);
        throw error;
      }
    };
    */
  }

  static trackDatabaseCalls() {
    // Track all database-related operations
    this.mark('database-operations-start');

    // Monitor any database service calls
    const dbMethods = ['query', 'connect', 'disconnect', 'init'];
    dbMethods.forEach(method => {
      console.log(`🔍 Monitoring database method: ${method}`);
    });
  }

  static trackComponentMount(componentName: string) {
    const markName = `${componentName}-mount-start`;
    this.mark(markName);

    // Return a function to call when component is fully mounted
    return () => {
      const measureName = `${componentName}-mount-complete`;
      this.measure(measureName, markName);
    };
  }

  static trackAsyncOperation(operationName: string) {
    const markName = `${operationName}-start`;
    this.mark(markName);

    return {
      end: () => {
        this.measure(operationName, markName);
      },
      step: (stepName: string) => {
        this.measure(`${operationName}-${stepName}`, markName);
      }
    };
  }

  static async detectVercelRegion() {
    try {
      // Try to detect region through geolocation and timing
      const startTime = performance.now();

      // Make a test request to see response headers
      const response = await fetch('/health', {
        method: 'GET',
        cache: 'no-cache'
      });

      const endTime = performance.now();
      const latency = endTime - startTime;

      // Check response headers for region info
      const headers: any = {};
      response.headers.forEach((value, key) => {
        if (key.toLowerCase().includes('region') ||
          key.toLowerCase().includes('location') ||
          key.toLowerCase().includes('server') ||
          key.toLowerCase().includes('edge')) {
          headers[key] = value;
        }
      });

      console.log('🗺️ Region Detection:', {
        healthCheckLatency: `${latency.toFixed(2)}ms`,
        responseHeaders: headers,
        status: response.status,
        url: response.url
      });

      // Estimate region based on latency
      let estimatedRegion = 'unknown';
      if (latency < 50) {
        estimatedRegion = 'same-region-or-cdn';
      } else if (latency < 200) {
        estimatedRegion = 'nearby-region';
      } else if (latency < 500) {
        estimatedRegion = 'distant-region';
      } else {
        estimatedRegion = 'very-distant-or-slow';
      }

      console.log(`🎯 Estimated Vercel Region: ${estimatedRegion} (${latency.toFixed(2)}ms latency)`);

      // Check if database is in a different region by testing a DB call
      if (process.env.NODE_ENV === 'production') {
        this.testDatabaseLatency();
      }

    } catch (error) {
      console.error('❌ Failed to detect Vercel region:', error);
    }
  }

  static async testDatabaseLatency() {
    try {
      const startTime = performance.now();

      // Test database connectivity using lightweight health check
      const response = await fetch('/health', {
        method: 'GET',
        cache: 'no-cache'
      });

      const endTime = performance.now();
      const dbLatency = endTime - startTime;

      console.log('🗄️ Database Latency Test:', {
        latency: `${dbLatency.toFixed(2)}ms`,
        status: response.status,
        databaseRegion: 'Sydney (ap-southeast-2)'
      });

      // Flag potential region mismatch
      if (dbLatency > 2000) {
        console.warn('⚠️ HIGH DATABASE LATENCY - Possible region mismatch!');
        console.warn('💡 Consider moving Vercel deployment to Sydney region (syd1)');
      }

    } catch (error) {
      console.error('❌ Database latency test failed:', error);
    }
  }
}

// Auto-start performance tracking
if (typeof window !== 'undefined') {
  PerformanceProfiler.trackLCP();
  PerformanceProfiler.trackResourceLoading();
  PerformanceProfiler.trackProductionBottlenecks();
  PerformanceProfiler.trackDatabaseCalls();

  // Track navigation timing after page load
  window.addEventListener('load', () => {
    setTimeout(() => {
      PerformanceProfiler.trackNavigationTiming();
      PerformanceProfiler.getReport();
    }, 100);
  });
}
